import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { NAV_ITEMS } from '../constants/nav';
import { visibleNavItems } from '../constants/features';
import './ProductTour.css';

// Welcome first (centred, no target), then one spotlight per sidebar entry so
// the reader sees exactly where each capability lives.
function buildSteps(user) {
  return [
    { key: 'welcome', tourKey: null, icon: null },
    ...visibleNavItems(NAV_ITEMS, user).map(({ tourKey, icon }) => ({ key: tourKey, tourKey, icon })),
  ];
}

const CALLOUT_WIDTH = 330;
const GAP = 14;

function ProductTour() {
  const { t } = useTranslation('common');
  const { user, updateUser } = useAuth();
  const [index, setIndex] = useState(0);
  // Rect of the highlighted sidebar item, or null when the step has no target
  // (welcome) or the sidebar isn't on screen (mobile) - then we centre instead.
  const [rect, setRect] = useState(null);

  // Driven purely by the flag on the account: updateUser writes it locally at
  // once, so finishing hides the tour instantly, and Settings can bring it back
  // by clearing the same flag.
  const show = !!user && !user.tour_completed;
  const steps = useMemo(() => buildSteps(user), [user]);
  const step = steps[index];

  const finish = useCallback(() => {
    updateUser({ tour_completed: true });
    api.updateUser(user.id, { tour_completed: true }).catch((err) => {
      console.error('Failed to persist tour completion:', err);
    });
  }, [user, updateUser]);

  // Always open on step one, including when replayed from Settings.
  useEffect(() => {
    if (show) setIndex(0);
  }, [show]);

  // Track the target's position: it shifts when the sidebar collapses, the
  // window resizes, or the page scrolls.
  useEffect(() => {
    if (!show) return undefined;
    const measure = () => {
      if (!step.tourKey) {
        setRect(null);
        return;
      }
      const el = document.querySelector(`[data-tour="${step.tourKey}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect(r.width && r.height ? r : null);
    };
    // A step's target can be inside a collapsed nav group (Conversations,
    // Others) - NavGroup listens for this and opens itself, then a second
    // measure (next frame, after that render lands) finds the real spot
    // instead of settling for the centred fallback.
    if (step.tourKey) window.dispatchEvent(new CustomEvent('tour-step-focus', { detail: step.tourKey }));
    measure();
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [show, step]);

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [show, finish]);

  if (!show) return null;

  const Icon = step.icon;
  const isLast = index === steps.length - 1;

  // Anchored beside the highlighted item; falls back to a centred card when
  // there's no target to point at.
  const calloutStyle = rect
    ? {
        top: Math.max(GAP, Math.min(rect.top + rect.height / 2, window.innerHeight - 220)),
        left: Math.min(rect.right + GAP, window.innerWidth - CALLOUT_WIDTH - GAP),
        transform: 'translateY(-50%)',
      }
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return createPortal(
    <div className="tour-root">
      {rect ? (
        <div
          className="tour-spotlight"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
        />
      ) : (
        <div className="tour-dim" onClick={finish} />
      )}

      <div className="tour-callout" style={calloutStyle} role="dialog" aria-modal="true">
        <button type="button" className="tour-close" onClick={finish} title={t('tour.skip')}>
          <X size={16} />
        </button>

        <div className="tour-heading">
          {Icon ? (
            <span className="tour-icon"><Icon size={18} /></span>
          ) : (
            <span className="tour-brand" aria-hidden="true">C</span>
          )}
          <h2 className="tour-title">{t(`tour.steps.${step.key}.title`)}</h2>
        </div>

        <p className="tour-body">{t(`tour.steps.${step.key}.body`)}</p>

        <div className="tour-actions">
          <button type="button" className="tour-skip" onClick={finish}>
            {t('tour.skip')}
          </button>
          <div className="tour-nav">
            <span className="tour-progress">
              {t('tour.progress', { current: index + 1, total: steps.length })}
            </span>
            {index > 0 && (
              <button type="button" className="tour-btn-ghost" onClick={() => setIndex(index - 1)}>
                {t('tour.back')}
              </button>
            )}
            <button
              type="button"
              className="tour-btn-primary"
              onClick={() => (isLast ? finish() : setIndex(index + 1))}
            >
              {isLast ? t('tour.done') : t('tour.next')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ProductTour;
